import React, { useState, useEffect, useMemo } from 'react';
import { X, Printer, Loader2 } from 'lucide-react';
import receiptsApi from './services/receiptsApi';

// ---------------------------------------------------------------------
// Thermal-printer preview for the two period-level receipts: Group
// Summary Receipt and Final Period Receipt. Both are the same document;
// `variant` ('summary' | 'final') controls the differences: the receipt
// number gets an "F" in front for the final copy, and the final copy
// adds Fines & Penalties, Advances Given, and Net Cash Remitted below
// the table. The "paper" itself is always rendered white-on-black
// regardless of app theme, like real thermal paper — only the dialog
// chrome around it follows dark mode.
//
// Group Summary Receipt totals (mpesaTotal/cashTotal/grandTotal) are
// computed straight from the real `receipts` prop passed down from
// ReceiptDetail.js — already real data, nothing to fetch here.
//
// Final Period Receipt's Fines & Penalties / Advances Given / Net Cash
// Remitted come from GET /api/receipt-periods/final-totals/, fetched
// only when variant='final' and the dialog opens — see
// mpesa/views/period_receipts_views.py for the (currently unwired)
// adapter this depends on.
//
// Still stubs, per instruction:
//   - Receipt numbers (mocked, deterministic per period id) — no backend
//     concept of a period-level receipt number exists yet.
//   - "Print Receipt" itself.
// Officer name now uses period.officerName (from GroupPerformancePeriod.officer)
// when the period carries one, falling back to the old mock otherwise.
// ---------------------------------------------------------------------

const OFFICER_NAMES = ['J. Mwangi', 'A. Otieno', 'S. Njeri', 'B. Kilonzo'];

const hashSeed = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) % 100000;
  }
  return hash;
};

const formatKSh = (amount) => `KSh ${Math.round(amount || 0).toLocaleString()}`;

const formatShortDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

// "MEM-1234" -> "1234" — the printed receipt shows "id: 1234", not the
// internal MEM- prefix.
const digitsOnly = (memberId) => (memberId || '').replace(/\D/g, '');

const PeriodReceiptDialog = ({ open, onClose, period, receipts, variant = 'summary' }) => {
  const isFinal = variant === 'final';

  const receiptNo = useMemo(() => {
    if (!period) return '';
    const seed = hashSeed(period.id || 'period');
    const base = `RCT-${String(seed).padStart(6, '0')}`;
    return isFinal ? `F${base}` : base;
  }, [period, isFinal]);

  // Real officer, when the period carries one (from GroupPerformancePeriod.officer
  // via list_receipt_periods) — falls back to the mock only if it's missing.
  const officerName = useMemo(() => {
    if (period?.officerName) return period.officerName;
    if (!period) return '';
    const seed = hashSeed(period.id || 'period');
    return OFFICER_NAMES[seed % OFFICER_NAMES.length];
  }, [period]);

  const [finalTotals, setFinalTotals] = useState(null);
  const [finalTotalsLoading, setFinalTotalsLoading] = useState(false);
  const [finalTotalsError, setFinalTotalsError] = useState(null);

  useEffect(() => {
    if (!open || !isFinal || !period) {
      setFinalTotals(null);
      setFinalTotalsError(null);
      return;
    }
    let cancelled = false;
    setFinalTotalsLoading(true);
    setFinalTotalsError(null);
    receiptsApi.fetchFinalPeriodTotals(period.group.id, period.meetingDate, period.type)
      .then((data) => { if (!cancelled) setFinalTotals(data); })
      .catch((err) => {
        console.error('Error loading final period totals:', err);
        if (!cancelled) setFinalTotalsError('Final period totals are not available yet.');
      })
      .finally(() => { if (!cancelled) setFinalTotalsLoading(false); });
    return () => { cancelled = true; };
  }, [open, isFinal, period?.id]);

  // Totals come from the receipt line items themselves, not the period's
  // separate cashRemitted/onlinePayments mock figures, so the printed
  // document always foots correctly against its own rows.
  const { mpesaTotal, cashTotal, grandTotal } = useMemo(() => {
    const list = receipts || [];
    const mpesa = list.filter(r => r.type === 'mpesa').reduce((s, r) => s + (r.amount || 0), 0);
    const cash = list.filter(r => r.type === 'cash').reduce((s, r) => s + (r.amount || 0), 0);
    return { mpesaTotal: mpesa, cashTotal: cash, grandTotal: mpesa + cash };
  }, [receipts]);

  const handlePrint = () => {
    alert(`Print ${isFinal ? 'Final Period Receipt' : 'Group Summary Receipt'} is not wired up yet — this is where the thermal printer integration hooks in.`);
  };

  if (!open || !period) return null;

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}></div>
      <div className="relative z-10 rounded-lg shadow-xl max-w-sm w-full bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            {isFinal ? 'Final Period Receipt' : 'Group Summary Receipt'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Thermal receipt "paper" — always black-on-white, monospace */}
        <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
          <div className="font-mono text-[13px] leading-snug text-black bg-white border border-dashed border-gray-300 rounded p-4">
            <div className="text-center">
              <p className="font-bold tracking-wide">LIFT SOLUTIONS</p>
              <p className="text-[11px] text-gray-600">{isFinal ? 'Final Period Receipt' : 'Group Summary Receipt'}</p>
            </div>
            <div className="border-t border-dashed border-gray-400 my-2"></div>

            <div className="flex justify-between"><span>Receipt No.</span><span className="font-semibold">{receiptNo}</span></div>
            <div className="flex justify-between"><span>Group</span><span className="text-right">{period.group.name}</span></div>
            <div className="flex justify-between"><span>Meeting Date</span><span>{formatShortDate(period.meetingDate)}</span></div>

            <div className="border-t border-dashed border-gray-400 my-2"></div>

            <div className="flex justify-between text-[15px] font-bold">
              <span>TOTAL AMOUNT</span>
              <span>{formatKSh(grandTotal)}</span>
            </div>

            <div className="border-t border-dashed border-gray-400 my-2"></div>

            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-gray-400">
                  <th className="text-left py-1">CUSTOMER</th>
                  <th className="text-right py-1">MPESA</th>
                  <th className="text-right py-1">CASH</th>
                </tr>
              </thead>
              <tbody>
                {(receipts || []).map((r, idx) => (
                  <tr key={r.id} className="align-top">
                    <td className="py-0.5 pr-1">#{idx + 1} {r.memberName} (id: {digitsOnly(r.memberId)})</td>
                    <td className="py-0.5 text-right">{r.type === 'mpesa' ? Math.round(r.amount).toLocaleString() : '—'}</td>
                    <td className="py-0.5 text-right">{r.type === 'cash' ? Math.round(r.amount).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-400 font-semibold">
                  <td className="pt-1">TOTAL</td>
                  <td className="pt-1 text-right">{mpesaTotal.toLocaleString()}</td>
                  <td className="pt-1 text-right">{cashTotal.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>

            {isFinal && (
              <>
                <div className="border-t border-dashed border-gray-400 my-2"></div>
                {finalTotalsLoading ? (
                  <div className="flex items-center justify-center py-3 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Loading totals...
                  </div>
                ) : finalTotalsError ? (
                  <p className="text-[11px] text-red-600">{finalTotalsError}</p>
                ) : (
                  <>
                    <div className="flex justify-between"><span>Fines &amp; Penalties</span><span className="font-semibold">{formatKSh(finalTotals?.finesAndPenalties)}</span></div>
                    <div className="flex justify-between"><span>Advances Given</span><span className="font-semibold">{formatKSh(finalTotals?.advancesGiven)}</span></div>
                    <div className="border-t border-dashed border-gray-400 my-2"></div>
                    <div className="flex justify-between"><span>Net Cash Remitted</span><span className="font-semibold">{formatKSh(finalTotals?.netCashRemitted)}</span></div>
                    <div className="flex justify-between"><span>Officer</span><span>{officerName}</span></div>
                  </>
                )}
              </>
            )}

            <div className="border-t border-dashed border-gray-400 my-2"></div>
            <p className="text-center text-[10px] text-gray-500">Thank you</p>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
};

export default PeriodReceiptDialog;